/**
 * AI 版桌面客户端「老版本引导升级」判定逻辑
 *
 * AI 版客户端（Electron）内嵌页的 UA 带版本标识，形如：
 *   今日水印相机-AI版/1.260825.2
 *
 * 客户端从 1.260824.2 起才内置自动更新能力，更早的版本装上后永远不会
 * 自己升级，只能引导用户手动下载安装最新版——这就是升级弹窗存在的原因，
 * 也是界限取 1.260824.2 的依据（弹窗文案「无法自动更新」只对这些版本成立）。
 * 1.260825.2 起 Windows 改用引导式更新器，那是更新方式的变化，不影响这条
 * 界限；若后续发现 Windows 旧更新链路实际不可用，再把 Windows 的界限单独
 * 提到 1.260825.2。
 */
import CONFIG from '../constants/config'
import { compareVersions } from './versionUtils'

/** 具备自动更新能力的最低客户端版本，低于它才弹升级引导 */
export const MIN_SELF_UPDATE_VERSION = '1.260824.2'

/**
 * localStorage key：用户最近一次「处理」弹窗（点关闭或点下载）的日期（YYYY-MM-DD）。
 * 只弹出、没点任何按钮不记账——客户端切菜单是同一个 iframe 换 src 重新加载，
 * 没处理过的话每个内嵌页都会再弹，直到用户点了关闭或下载，当天才不再弹。
 */
export const LS_AI_CLIENT_UPGRADE_HANDLED_DATE =
  'aiClientUpgradeModal_handledDate'

// 客户端应用名先后有两个：v1.260812.1 起 app.setName 用「今日水印相机-AI版」，
// 更早的 0.8.x 线上版本用「TodayWork」。旧名的客户端版本必然低于界限，
// 恰恰是最需要引导升级的一批，所以两个标识都要认。
const AI_CLIENT_UA_RE = /(?:今日水印相机-AI版|TodayWork)\/(\d+(?:\.\d+)*)/

/** 本地时区的当天日期串（YYYY-MM-DD），作为「一天一次」的比较基准 */
function currentDateStr(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 从 UA 解析 AI 版客户端版本号；非 AI 版客户端环境返回 null */
export function parseAIClientVersion(ua: string): string | null {
  const matched = ua.match(AI_CLIENT_UA_RE)
  return matched ? matched[1] : null
}

/** 按 UA 选客户端下载地址：Windows 一个入口，其余按 Mac（arm64）处理 */
export function getAIClientDownloadURL(
  ua: string = navigator.userAgent
): string {
  return /Windows/i.test(ua)
    ? CONFIG.AI_DESKTOP_DOWNLOAD_URL
    : CONFIG.AI_DESKTOP_DOWNLOAD_URL_MAC
}

/**
 * 是否应展示升级弹窗：AI 版客户端 + 版本低于自动更新界限 + 今天还没处理过。
 * ua / today 可注入，便于单测；生产调用不传参。
 */
export function shouldShowAIClientUpgradeModal(
  ua: string = navigator.userAgent,
  today: string = currentDateStr()
): boolean {
  const version = parseAIClientVersion(ua)
  if (!version) return false
  if (compareVersions(version, MIN_SELF_UPDATE_VERSION) >= 0) return false
  try {
    return localStorage.getItem(LS_AI_CLIENT_UPGRADE_HANDLED_DATE) !== today
  } catch (_e) {
    // localStorage 不可用时宁可不弹，避免同一会话反复骚扰
    return false
  }
}

/** 记录今天已处理（点了关闭或下载），当天内不再弹 */
export function markAIClientUpgradeModalHandled(
  today: string = currentDateStr()
): void {
  try {
    localStorage.setItem(LS_AI_CLIENT_UPGRADE_HANDLED_DATE, today)
  } catch (_e) {
    /* localStorage may be blocked */
  }
}
